/**
 * Client → Strategy — hybrid wizard + staged AI pipeline.
 *
 * See /app/memory/PRD.md · "Strategy Planner v2" for architecture
 * context. This page owns the full 8-step user flow:
 *
 *   1. Objective  →  Stage 0 intake (LLM)
 *   2. Channel mix approval
 *   3. Scope (target pages, competitors, seed keywords)
 *   4. Confirm data pulls (the transparency step)
 *   5. Build progress (Stages 1-4)
 *   6. Editable workspace (sections)
 *   7. WYSIWYG preview + positive-framing gate
 *   8. Approve → PDF export
 *
 * The list mode above the wizard shows existing v2 strategies and lets
 * the admin open one back into the workspace / preview steps.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Sparkles, Loader2, ArrowLeft, ArrowRight, Plus, CheckCircle2,
  Wand2, Eye, FileDown, RefreshCw, AlertTriangle, Layers, MessageSquarePlus,
} from "lucide-react";

const STEP_LABELS = [
  "Objective", "Channel mix", "Scope", "Data plan", "Build",
  "Workspace", "Preview", "Export",
];

export default function ClientStrategyV2Page() {
  const { clientId } = useParams();
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);   // strategy doc
  const [loading, setLoading] = useState(true);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/strategy-v2/clients/${clientId}/strategies`);
      setList(data.strategies || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load strategies");
    } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { loadList(); }, [loadList]);

  const openStrategy = async (sid) => {
    try {
      const { data } = await api.get(`/strategy-v2/strategies/${sid}`);
      setSelected(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to open strategy");
    }
  };

  if (selected) {
    return (
      <StrategyWizard clientId={clientId} initial={selected}
        onClose={() => { setSelected(null); loadList(); }}
        onUpdate={(fresh) => setSelected(fresh)} />
    );
  }

  return (
    <div className="space-y-4" data-testid="strategy-v2-page">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span className="h-8 w-8 rounded-sm grid place-items-center bg-black/[0.06]">
            <Sparkles className="h-4 w-4 text-black" />
          </span>
          <div>
            <h2 className="font-display font-bold text-xl">Strategy</h2>
            <p className="text-[12px] text-zinc-500">
              Staged AI pipeline — objective in, editable whitepaper out. Real live-data pulls activate in Phase&nbsp;1.1.
            </p>
          </div>
        </div>
        <NewStrategyButton clientId={clientId} onCreated={(s) => setSelected(s)} />
      </div>

      {loading ? (
        <Card className="rounded-sm"><CardContent className="p-6 flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading strategies…
        </CardContent></Card>
      ) : list.length === 0 ? (
        <Card className="rounded-sm border-dashed"><CardContent className="p-10 text-center text-sm text-zinc-500"
          data-testid="strategy-v2-empty">
          <Sparkles className="h-6 w-6 mx-auto mb-2 text-zinc-300" />
          No strategies yet. Click <strong>New strategy</strong> to run the pipeline.
        </CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="strategy-v2-list">
          {list.map((s) => <StrategyRow key={s.strategy_id} s={s} onOpen={() => openStrategy(s.strategy_id)} />)}
        </div>
      )}
    </div>
  );
}

function StrategyRow({ s, onOpen }) {
  const badgeCls = {
    draft: "bg-zinc-100 text-zinc-700 border-zinc-200",
    building: "bg-blue-50 text-blue-700 border-blue-200",
    workspace: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  }[s.status] || "bg-zinc-100 text-zinc-700";
  return (
    <Card className="rounded-sm hover:border-black/40 transition-colors cursor-pointer"
      onClick={onOpen} data-testid={`strategy-v2-row-${s.strategy_id}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">{s.objective}</div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge variant="outline" className={`rounded-sm text-[9px] ${badgeCls}`}>{s.status?.toUpperCase()}</Badge>
            <Badge variant="outline" className="rounded-sm text-[9px] font-mono">step {s.step}/8</Badge>
            <span className="text-[10px] font-mono text-zinc-500">{s.domain}</span>
            <span className="text-[10px] font-mono text-zinc-400">{(s.created_at || "").slice(0, 10)}</span>
          </div>
        </div>
        <ArrowRight className="h-4 w-4 text-zinc-300" />
      </CardContent>
    </Card>
  );
}

function NewStrategyButton({ clientId, onCreated }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" className="rounded-sm bg-black hover:bg-zinc-800 text-white h-8"
        onClick={() => setOpen(true)} data-testid="strategy-v2-new-btn">
        <Plus className="h-3.5 w-3.5 mr-1" /> New strategy
      </Button>
      {open && <IntakeModal clientId={clientId} onClose={() => setOpen(false)} onCreated={onCreated} />}
    </>
  );
}

function IntakeModal({ clientId, onClose, onCreated }) {
  const [objective, setObjective] = useState("");
  const [domain, setDomain] = useState("");
  const [geo, setGeo] = useState("AU");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (objective.trim().length < 5) { toast.error("Give a proper objective (5+ chars)"); return; }
    if (domain.trim().length < 3) { toast.error("Domain is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/strategy-v2/clients/${clientId}/strategies`,
        { objective, domain, geo });
      toast.success("Intake analysed — review the channel mix");
      onClose(); onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Intake failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="strategy-v2-intake-modal">
      <Card className="rounded-sm w-full max-w-lg"><CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-black" />
          <h3 className="font-semibold">Step 1 · Objective</h3></div>
        <p className="text-[12px] text-zinc-500">Plain-language objective. The AI will classify intent and propose a coordinated channel mix (Stage 0).</p>
        <div>
          <Label className="text-[11px] font-mono uppercase text-zinc-500">Objective</Label>
          <Textarea value={objective} onChange={(e) => setObjective(e.target.value)}
            placeholder="e.g. Get the switchboard upgrades page onto page 1 in AU."
            rows={3} data-testid="strategy-v2-objective-input" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[11px] font-mono uppercase text-zinc-500">Domain</Label>
            <Input value={domain} onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com.au" data-testid="strategy-v2-domain-input" />
          </div>
          <div>
            <Label className="text-[11px] font-mono uppercase text-zinc-500">Geo</Label>
            <Input value={geo} onChange={(e) => setGeo(e.target.value)}
              placeholder="AU" data-testid="strategy-v2-geo-input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="rounded-sm bg-black hover:bg-zinc-800 text-white"
            onClick={submit} disabled={busy} data-testid="strategy-v2-intake-submit">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Wand2 className="h-3.5 w-3.5 mr-1" />}
            Analyse objective
          </Button>
        </div>
      </CardContent></Card>
    </div>
  );
}

/* ============================== WIZARD =================================== */

function StrategyWizard({ initial, onClose, onUpdate }) {
  const [strategy, setStrategy] = useState(initial);
  const [stepOverride, setStepOverride] = useState(null);
  const step = stepOverride ?? strategy.step ?? 2;
  const refresh = useCallback(async () => {
    const { data } = await api.get(`/strategy-v2/strategies/${strategy.strategy_id}`);
    setStrategy(data); onUpdate(data);
  }, [strategy.strategy_id, onUpdate]);
  const goToStep = (n) => setStepOverride(n);

  return (
    <div className="space-y-4" data-testid="strategy-v2-wizard">
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={onClose}
          data-testid="strategy-v2-back-to-list">
          <ArrowLeft className="h-3 w-3 mr-1" /> All strategies
        </Button>
        <span className="text-[12px] text-zinc-500 truncate max-w-lg">{strategy.objective}</span>
        <Badge variant="outline" className="rounded-sm text-[9px] font-mono ml-auto">
          Step {step}/8 · {STEP_LABELS[step - 1] || ""}
        </Badge>
      </div>
      <Stepper current={step} onJump={(n) => n < step && goToStep(n)} />

      {step === 2 && <StepChannels strategy={strategy} onDone={() => { setStepOverride(null); refresh(); }} />}
      {step === 3 && <StepScope strategy={strategy} onDone={() => { setStepOverride(null); refresh(); }} />}
      {step === 4 && <StepDataPlan strategy={strategy} onDone={() => { setStepOverride(null); refresh(); }} />}
      {step === 5 && <StepBuild strategy={strategy} onDone={() => { setStepOverride(null); refresh(); }} />}
      {step === 6 && <StepWorkspace strategy={strategy} onPreview={() => goToStep(7)} />}
      {step === 7 && <StepPreview strategy={strategy}
                                   onBack={() => goToStep(6)}
                                   onRebuild={() => { setStepOverride(null); refresh(); }}
                                   onApproved={() => { setStepOverride(null); refresh(); }} />}
      {step === 8 && <StepExport strategy={strategy} />}
    </div>
  );
}

function Stepper({ current, onJump }) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="strategy-v2-stepper">
      {STEP_LABELS.map((label, i) => {
        const n = i + 1;
        const done = n < current;
        const active = n === current;
        const clickable = done && onJump;
        return (
          <button key={label} type="button" disabled={!clickable}
            onClick={() => clickable && onJump(n)}
            className={`text-[10px] font-mono px-2 py-1 rounded-sm whitespace-nowrap ${
              active ? "bg-black text-white" :
              done ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200 cursor-pointer" :
              "bg-zinc-100 text-zinc-400"}`}>
            {n}. {label}
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Step 2: Channel mix ---------- */
function StepChannels({ strategy, onDone }) {
  const [mode, setMode] = useState(strategy.mode || "single");
  const [catalog, setCatalog] = useState(null);
  const initialTypes = useMemo(() => {
    const explicit = (strategy.channels || []).map((c) => c.type);
    if (explicit.length) return explicit;
    return (strategy.recommended_mix || []).map((c) => c.type);
  }, [strategy]);
  const [selectedTypes, setSelectedTypes] = useState(new Set(initialTypes));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/strategy-v2/channels").then(({ data }) => setCatalog(data))
      .catch((e) => toast.error(e.response?.data?.detail || "Channel catalog failed"));
  }, []);

  // Once the catalog loads, drop any pre-selected types that aren't real
  // channels (e.g. umbrella `organic_social` from a playbook rule).
  useEffect(() => {
    if (!catalog) return;
    const validTypes = new Set((catalog.channels || []).map((c) => c.type));
    setSelectedTypes((prev) => {
      const next = new Set(Array.from(prev).filter((t) => validTypes.has(t)));
      return next.size === prev.size ? prev : next;
    });
  }, [catalog]);

  const toggle = (t) => {
    const next = new Set(selectedTypes);
    if (next.has(t)) next.delete(t); else next.add(t);
    setSelectedTypes(next);
  };

  const save = async () => {
    if (selectedTypes.size === 0) {
      toast.error("Select at least one channel");
      return;
    }
    setBusy(true);
    try {
      // Preserve any playbook-provided role/priority/sequence for channels
      // that were in the AI's recommended mix; use sensible defaults for
      // the rest so the pipeline has everything it needs.
      const recommendedByType = new Map(
        (strategy.recommended_mix || []).map((c) => [c.type, c]));
      const channels = Array.from(selectedTypes).map((t, idx) => {
        const rec = recommendedByType.get(t);
        return rec ? { ...rec, priority: rec.priority || (idx + 1) }
                   : { type: t, role: "primary", priority: idx + 1,
                       sequence: "manual add", why: "strategist selection" };
      });
      const finalMode = channels.length > 1 ? "multi" : "single";
      await api.patch(`/strategy-v2/strategies/${strategy.strategy_id}/channels`,
        { mode: finalMode, channels });
      toast.success(`${channels.length} channel${channels.length > 1 ? "s" : ""} saved`);
      await onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!catalog) return <Card className="rounded-sm"><CardContent className="p-5 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading channels…</CardContent></Card>;

  const recommendedTypes = new Set((strategy.recommended_mix || []).map((c) => c.type));

  return (
    <Card className="rounded-sm"><CardContent className="p-5 space-y-4" data-testid="strategy-v2-step2">
      <StepHeader n={2} title="Pick your channels"
        subtitle="Select every platform this strategy should cover. The AI has starred what it recommends based on your objective + playbook — but you decide." />

      <div className="text-[11px] text-zinc-500 flex items-center gap-3 flex-wrap">
        <span>Selected: <strong className="font-mono text-zinc-900">{selectedTypes.size}</strong></span>
        <span>Mode: <strong className="font-mono text-zinc-900">{selectedTypes.size > 1 ? "multi-channel" : "single-channel"}</strong></span>
        {strategy.rules_fired?.length > 0 && (
          <span className="text-[10px] font-mono">Playbook rules fired: {strategy.rules_fired.join(", ")}</span>
        )}
      </div>

      <div className="space-y-3">
        {(catalog.groups || []).map((group) => {
          const inGroup = (catalog.channels || []).filter((c) => c.group === group);
          if (!inGroup.length) return null;
          return (
            <div key={group}>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">{group}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {inGroup.map((c) => {
                  const on = selectedTypes.has(c.type);
                  const recommended = recommendedTypes.has(c.type);
                  return (
                    <label key={c.type}
                      className={`border rounded-sm p-2.5 cursor-pointer transition-colors ${
                        on ? "border-black bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}
                      data-testid={`strategy-v2-channel-${c.type}`}>
                      <div className="flex items-start gap-2">
                        <input type="checkbox" className="mt-1 accent-black" checked={on}
                          onChange={() => toggle(c.type)} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[13px] font-semibold">{c.label}</span>
                            {recommended && (
                              <Badge variant="outline" className="rounded-sm text-[8px] bg-black text-white border-black">
                                RECOMMENDED
                              </Badge>
                            )}
                          </div>
                          <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                            {c.data_sources.join(" · ")} · {c.section_count} sections
                          </div>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <NavButtons onNext={save} nextLabel={`Save ${selectedTypes.size} channel${selectedTypes.size === 1 ? "" : "s"} & continue`}
        nextBusy={busy} nextDisabled={selectedTypes.size === 0}
        testidPrefix="strategy-v2-step2" />
    </CardContent></Card>
  );
}

/* ---------- Step 3: Scope ---------- */
function StepScope({ strategy, onDone }) {
  const [targetPages, setTargetPages] = useState((strategy.target_pages || []).join("\n"));
  const [competitors, setCompetitors] = useState((strategy.competitors || []).join("\n"));
  const [seedKeywords, setSeedKeywords] = useState((strategy.seed_keywords || strategy.brief?.seed_keywords || []).join("\n"));
  const [geo, setGeo] = useState(strategy.geo || "AU");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/strategy-v2/strategies/${strategy.strategy_id}/scope`, {
        target_pages: targetPages.split("\n").map((s) => s.trim()).filter(Boolean),
        competitors: competitors.split("\n").map((s) => s.trim()).filter(Boolean),
        seed_keywords: seedKeywords.split("\n").map((s) => s.trim()).filter(Boolean),
        geo,
      });
      toast.success("Scope saved");
      await onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="rounded-sm"><CardContent className="p-5 space-y-4" data-testid="strategy-v2-step3">
      <StepHeader n={3} title="Scope the targets"
        subtitle="Shared across every selected channel — one entry per line." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] font-mono uppercase text-zinc-500">Target pages / URLs</Label>
          <Textarea value={targetPages} onChange={(e) => setTargetPages(e.target.value)}
            rows={4} placeholder={`https://${strategy.domain}/services/switchboards`}
            data-testid="strategy-v2-target-pages" />
        </div>
        <div>
          <Label className="text-[11px] font-mono uppercase text-zinc-500">Seed keywords / topics</Label>
          <Textarea value={seedKeywords} onChange={(e) => setSeedKeywords(e.target.value)}
            rows={4} placeholder="switchboard upgrades" data-testid="strategy-v2-seed-keywords" />
        </div>
        <div>
          <Label className="text-[11px] font-mono uppercase text-zinc-500">Competitors (domains)</Label>
          <Textarea value={competitors} onChange={(e) => setCompetitors(e.target.value)}
            rows={4} placeholder="competitor1.com.au" data-testid="strategy-v2-competitors" />
        </div>
        <div>
          <Label className="text-[11px] font-mono uppercase text-zinc-500">Geo / market</Label>
          <Input value={geo} onChange={(e) => setGeo(e.target.value)}
            data-testid="strategy-v2-geo" />
        </div>
      </div>
      <NavButtons onNext={save} nextLabel="Save & continue" nextBusy={busy}
        testidPrefix="strategy-v2-step3" />
    </CardContent></Card>
  );
}

/* ---------- Step 4: Data plan ---------- */
function StepDataPlan({ strategy, onDone }) {
  const [plan, setPlan] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/strategy-v2/strategies/${strategy.strategy_id}/data-plan`)
      .then(({ data }) => {
        setPlan(data);
        setSelected(new Set((data.items || []).filter((i) => i.default).map((i) => i.key)));
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Data-plan load failed"));
  }, [strategy.strategy_id]);

  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelected(next);
  };

  const build = async () => {
    setBusy(true);
    try {
      await api.post(`/strategy-v2/strategies/${strategy.strategy_id}/build`,
        { included_pull_keys: Array.from(selected) });
      toast.success("Build started");
      await onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Build failed");
    } finally { setBusy(false); }
  };

  if (!plan) return <Card className="rounded-sm"><CardContent className="p-5 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading data plan…</CardContent></Card>;

  return (
    <Card className="rounded-sm"><CardContent className="p-5 space-y-4" data-testid="strategy-v2-step4">
      <StepHeader n={4} title="Confirm data to pull"
        subtitle="Transparency step — trim any source you don't want the AI to reason over. v1 pulls fixture data (deterministic); live Ahrefs/Semrush/GSC/GA4 activates in Phase 1.1." />
      <div className="space-y-1.5" data-testid="strategy-v2-pulls-list">
        {(plan.items || []).map((it) => (
          <label key={it.key} className="flex items-start gap-2 border border-zinc-200 rounded-sm p-2.5 cursor-pointer hover:bg-zinc-50"
            data-testid={`strategy-v2-pull-${it.key}`}>
            <input type="checkbox" className="mt-1 accent-black"
              checked={selected.has(it.key)} onChange={() => toggle(it.key)} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-mono font-semibold text-black">{it.source}</span>
                <span className="text-[11px] font-mono text-zinc-600">{it.endpoint}</span>
                <span className="text-[10px] font-mono text-zinc-400 ml-auto">~{Math.round(it.estimated_ms / 1000)}s</span>
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">{it.why}</div>
            </div>
          </label>
        ))}
      </div>
      <NavButtons onNext={build} nextLabel="Pull & generate" nextBusy={busy}
        nextIcon={<Wand2 className="h-3.5 w-3.5 mr-1" />}
        testidPrefix="strategy-v2-step4" />
    </CardContent></Card>
  );
}

/* ---------- Step 5: Build progress (SSE) ---------- */
function StepBuild({ strategy, onDone }) {
  const [progress, setProgress] = useState(strategy.build_progress || "Starting…");
  const [error, setError] = useState(null);
  const [log, setLog] = useState([]);
  const [retrying, setRetrying] = useState(false);
  const retry = async () => {
    setRetrying(true);
    try {
      await api.post(`/strategy-v2/strategies/${strategy.strategy_id}/build`, {});
      toast.success("Restarted build");
      setError(null); setProgress("Starting…"); setLog([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally { setRetrying(false); }
  };
  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const url = `${backendUrl}/api/strategy-v2/strategies/${strategy.strategy_id}/build-stream`;
    // Bearer auth doesn't work with native EventSource; fetch-stream fallback.
    const controller = new AbortController();
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const resp = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        });
        const reader = resp.body?.getReader();
        if (!reader) throw new Error("No stream reader");
        const decoder = new TextDecoder();
        let buf = "";
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const events = buf.split("\n\n");
          buf = events.pop() || "";
          for (const evt of events) {
            const dataLine = evt.split("\n").find((l) => l.startsWith("data:"));
            if (!dataLine) continue;
            try {
              const payload = JSON.parse(dataLine.slice(5).trim() || "{}");
              if (payload.progress) {
                setProgress(payload.progress);
                setLog((l) => [...l.slice(-40), payload.progress]);
              }
              if (payload.status === "build_failed") {
                setError(payload.error || "Build failed");
              } else if (payload.status === "workspace") {
                toast.success("Build complete — review the editable workspace");
                onDone();
                return;
              }
            } catch { /* ignore parse errors */ }
          }
        }
      } catch (e) {
        // Fall back to polling if SSE fails (network / auth quirk)
        if (e.name !== "AbortError") {
          const iv = setInterval(async () => {
            try {
              const { data } = await api.get(`/strategy-v2/strategies/${strategy.strategy_id}`);
              if (data.build_progress) {
                setProgress(data.build_progress);
                setLog((l) => [...l.slice(-40), data.build_progress]);
              }
              if (data.status === "build_failed") {
                clearInterval(iv);
                setError(data.build_error || "Build failed");
              } else if (data.status === "workspace" || data.step >= 6) {
                clearInterval(iv);
                onDone();
              }
            } catch { /* keep polling */ }
          }, 2500);
        }
      }
    })();
    return () => controller.abort();
  }, [strategy.strategy_id, onDone]);
  return (
    <Card className="rounded-sm"><CardContent className="p-6 space-y-4" data-testid="strategy-v2-step5">
      {error ? (
        <div className="text-center space-y-2">
          <AlertTriangle className="h-6 w-6 mx-auto text-red-500" />
          <div className="text-sm font-semibold text-red-700">Build failed</div>
          <div className="text-[11px] text-zinc-500 max-w-md mx-auto font-mono">{error}</div>
          <div className="text-[11px] text-zinc-500">The strategy is preserved — click below to retry, or jump back to Step 4 (Data plan) via the stepper above.</div>
          <div className="pt-2">
            <Button size="sm" className="rounded-sm h-8 bg-black hover:bg-zinc-800 text-white"
              onClick={retry} disabled={retrying}
              data-testid="strategy-v2-build-retry">
              {retrying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              Retry build
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 text-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin text-black" />
            <div className="text-sm font-medium" data-testid="strategy-v2-build-progress">{progress}</div>
          </div>
          <div className="text-[11px] text-zinc-500 text-center max-w-md mx-auto">
            Diagnosis + Synthesis + Drafting per channel · Cross-channel roadmap woven when 2+ channels chosen · You can leave this screen — the build runs on the server.
          </div>
          {log.length > 0 && (
            <div className="mt-3 max-h-40 overflow-y-auto border border-zinc-200 rounded-sm bg-black text-green-400 font-mono text-[10px] p-2 space-y-0.5"
              data-testid="strategy-v2-build-log">
              {log.map((line, i) => (
                <div key={i}>&gt; {line}</div>
              ))}
            </div>
          )}
        </>
      )}
    </CardContent></Card>
  );
}

/* ---------- Step 6: Editable workspace ---------- */
function StepWorkspace({ strategy, onPreview }) {
  const [payload, setPayload] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get(`/strategy-v2/strategies/${strategy.strategy_id}/workspace`);
    setPayload(data);
  }, [strategy.strategy_id]);
  useEffect(() => { load(); }, [load]);

  if (!payload) return <Card className="rounded-sm"><CardContent className="p-5 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading workspace…</CardContent></Card>;

  // Group sections by channel for the new multi-channel view.
  const byChannel = {};
  (payload.sections || []).forEach((s) => {
    const key = s.channel || "seo";
    if (!byChannel[key]) byChannel[key] = [];
    byChannel[key].push(s);
  });
  const channelOrder = (payload.strategy?.channels || []).map((c) => c.type)
    .filter((t) => byChannel[t]);
  Object.keys(byChannel).forEach((k) => {
    if (!channelOrder.includes(k)) channelOrder.push(k);
  });

  return (
    <Card className="rounded-sm"><CardContent className="p-5 space-y-4" data-testid="strategy-v2-step6">
      <StepHeader n={6} title="Editable strategy workspace"
        subtitle="Sections drafted per channel. Edit inline or regenerate individual sections with an instruction." />

      {payload.snapshot?.mode && (
        <div className="text-[10px] font-mono text-zinc-500 border border-dashed border-zinc-300 rounded-sm px-2.5 py-1.5 inline-block">
          Data mode: <strong className="text-zinc-900">{payload.snapshot.mode}</strong>
          {payload.snapshot.mode === "fixture" &&
            " — live pulls fell back to fixture data. See the Data plan step for details."}
        </div>
      )}

      <div className="space-y-4" data-testid="strategy-v2-sections-list">
        {channelOrder.map((channelType) => (
          <div key={channelType} className="border border-zinc-300 rounded-sm">
            <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50">
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Channel</div>
              <div className="text-[13px] font-semibold">{prettyChannel(channelType)}</div>
            </div>
            <div className="p-2 space-y-2">
              {byChannel[channelType].map((s) => (
                <SectionEditor key={s.section_id} section={s} onChanged={load} />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
        <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={load}
          data-testid="strategy-v2-workspace-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reload
        </Button>
        <Button size="sm" className="rounded-sm h-8 bg-black hover:bg-zinc-800 text-white"
          onClick={onPreview}
          data-testid="strategy-v2-workspace-next">
          <Eye className="h-3.5 w-3.5 mr-1" /> Preview whitepaper
        </Button>
      </div>
    </CardContent></Card>
  );
}

function prettyChannel(t) {
  return ({
    seo: "Website SEO",
    blog: "Website Blog / Content",
    instagram: "Instagram (organic)",
    facebook: "Facebook (organic)",
    linkedin: "LinkedIn (organic)",
    tiktok: "TikTok (organic)",
    x_twitter: "X / Twitter (organic)",
    pinterest: "Pinterest (organic)",
    email: "Email Marketing",
    paid_search: "Paid Search (Google Ads)",
    paid_social: "Paid Social & Display",
    video_ads: "Video Ads (YouTube)",
    ai_search_geo: "AI Search / GEO",
  }[t]) || t;
}

function SectionEditor({ section, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [html, setHtml] = useState(section.content_html || "");
  const [busy, setBusy] = useState(false);
  const [instruction, setInstruction] = useState("");
  const flags = section.framing_flags || [];

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/strategy-v2/sections/${section.section_id}`, { content_html: html });
      toast.success("Section saved");
      await onChanged();
      setExpanded(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const regen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/strategy-v2/sections/${section.section_id}/regenerate`,
        { instruction: instruction || undefined });
      setHtml(data.content_html);
      toast.success("Section regenerated");
      await onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Regenerate failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-zinc-200 rounded-sm" data-testid={`strategy-v2-section-${section.key}`}>
      <button type="button" onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-zinc-50 hover:bg-zinc-100 text-left">
        <Layers className="h-3.5 w-3.5 text-zinc-400" />
        <span className="text-[13px] font-semibold flex-1">{section.title}</span>
        {section.edited_by_user && <Badge variant="outline" className="rounded-sm text-[8px] bg-blue-50 text-blue-700 border-blue-200">EDITED</Badge>}
        {flags.length > 0 && (
          <Badge variant="outline" className="rounded-sm text-[8px] bg-amber-50 text-amber-700 border-amber-200"
            title={`Positive-framing flags: ${flags.join(", ")}`}>
            <AlertTriangle className="h-3 w-3 mr-1" /> {flags.length}
          </Badge>
        )}
        <ArrowRight className={`h-3.5 w-3.5 text-zinc-400 transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>
      {expanded && (
        <div className="p-3 space-y-2">
          <div className="prose prose-sm max-w-none border border-dashed border-zinc-200 p-3 rounded-sm bg-white"
            dangerouslySetInnerHTML={{ __html: html }} />
          <Textarea value={html} onChange={(e) => setHtml(e.target.value)}
            rows={6} className="font-mono text-[11px]"
            data-testid={`strategy-v2-section-edit-${section.key}`} />
          <div className="flex items-center gap-2 flex-wrap">
            <Input placeholder="Regenerate instruction (optional) — e.g. 'make it more concise'"
              value={instruction} onChange={(e) => setInstruction(e.target.value)}
              className="flex-1 text-[11px] font-mono h-8" />
            <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={regen} disabled={busy}
              data-testid={`strategy-v2-section-regen-${section.key}`}>
              {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Wand2 className="h-3 w-3 mr-1" />}
              Regenerate
            </Button>
            <Button size="sm" className="rounded-sm h-8 bg-zinc-900 hover:bg-black text-white" onClick={save} disabled={busy}
              data-testid={`strategy-v2-section-save-${section.key}`}>
              Save
            </Button>
          </div>
          {flags.length > 0 && (
            <div className="text-[10px] font-mono text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
              Positive-framing rule flags: <strong>{flags.join(", ")}</strong>. Export is blocked until these are reframed.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- Step 7: Preview + change request + approve ---------- */
function StepPreview({ strategy, onBack, onApproved, onRebuild }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [crBusy, setCrBusy] = useState(false);
  const [changeRequest, setChangeRequest] = useState("");
  const load = useCallback(async () => {
    const { data } = await api.get(`/strategy-v2/strategies/${strategy.strategy_id}/preview`);
    setPreview(data);
  }, [strategy.strategy_id]);
  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/strategy-v2/strategies/${strategy.strategy_id}/approve`);
      toast.success("Strategy approved — ready to export");
      onApproved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval blocked");
    } finally { setBusy(false); }
  };

  const submitChangeRequest = async () => {
    if (changeRequest.trim().length < 5) {
      toast.error("Give a clearer instruction (5+ chars)");
      return;
    }
    setCrBusy(true);
    try {
      await api.post(`/strategy-v2/strategies/${strategy.strategy_id}/change-request`,
        { instruction: changeRequest.trim() });
      toast.success("Rebuilding with your change request…");
      onRebuild();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Change request failed");
    } finally { setCrBusy(false); }
  };

  if (!preview) return <Card className="rounded-sm"><CardContent className="p-5 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Rendering preview…</CardContent></Card>;

  const hasFlags = (preview.framing_flags || []).length > 0;

  return (
    <Card className="rounded-sm"><CardContent className="p-5 space-y-3" data-testid="strategy-v2-step7">
      <StepHeader n={7} title="Full preview & approval"
        subtitle="Black-and-white A4 whitepaper. Positive-framing rule is enforced — approval blocks while any flags remain." />

      {hasFlags && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2.5 flex items-start gap-2"
          data-testid="strategy-v2-preview-flags">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <strong>Positive-framing rule flagged {preview.framing_flags.length} phrase(s):</strong>{" "}
            {preview.framing_flags.join(", ")}. Go back to Step 6 and reframe the highlighted sections before approval.
          </div>
        </div>
      )}

      <iframe title="Whitepaper preview" srcDoc={preview.html}
        className="w-full border border-zinc-300 rounded-sm bg-zinc-100"
        style={{ height: "72vh" }} data-testid="strategy-v2-preview-iframe" />

      <div className="border border-zinc-200 rounded-sm p-3 space-y-2 bg-zinc-50" data-testid="strategy-v2-change-request">
        <div className="flex items-center gap-2">
          <MessageSquarePlus className="h-4 w-4 text-black" />
          <div className="text-[12px] font-semibold">Change request</div>
          <div className="text-[10px] font-mono text-zinc-500 ml-auto">rebuilds the whole strategy with your note</div>
        </div>
        <Textarea value={changeRequest} onChange={(e) => setChangeRequest(e.target.value)}
          rows={3} placeholder="e.g. Emphasise B2B decision-makers on LinkedIn. Reduce blog volume, prioritise depth over quantity."
          data-testid="strategy-v2-change-request-input" className="text-[12px]" />
        <div className="flex justify-end">
          <Button size="sm" variant="outline" className="rounded-sm h-8 border-black"
            onClick={submitChangeRequest} disabled={crBusy || changeRequest.trim().length < 5}
            data-testid="strategy-v2-change-request-submit">
            {crBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Wand2 className="h-3.5 w-3.5 mr-1" />}
            Rebuild with this instruction
          </Button>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
        <Button size="sm" variant="outline" className="rounded-sm h-8"
          onClick={onBack} data-testid="strategy-v2-preview-back">
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to workspace
        </Button>
        <Button size="sm" className="rounded-sm h-8 bg-black hover:bg-zinc-800 text-white"
          onClick={approve} disabled={busy || hasFlags}
          data-testid="strategy-v2-approve-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
          Approve & continue to export
        </Button>
      </div>
    </CardContent></Card>
  );
}

/* ---------- Step 8: Export ---------- */
function StepExport({ strategy }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const resp = await api.post(`/strategy-v2/strategies/${strategy.strategy_id}/export`,
        {}, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${strategy.workspace?.name || "strategy"}-seo-strategy.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success("Exported");
    } catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="rounded-sm"><CardContent className="p-8 text-center space-y-3" data-testid="strategy-v2-step8">
      <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto" />
      <div className="text-lg font-semibold">Approved — ready to export</div>
      <div className="text-[12px] text-zinc-500 max-w-md mx-auto">
        The whitepaper renders from your approved sections via headless Chrome. The client-facing PDF respects the positive-framing rule and cover metadata.
      </div>
      <Button size="sm" className="rounded-sm h-9 bg-black hover:bg-zinc-800 text-white"
        onClick={download} disabled={busy} data-testid="strategy-v2-export-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FileDown className="h-4 w-4 mr-1.5" />}
        Download PDF
      </Button>
    </CardContent></Card>
  );
}

/* ---------- Shared UI ---------- */
function StepHeader({ n, title, subtitle }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase text-black">Step {n}</div>
      <h3 className="font-semibold text-base">{title}</h3>
      {subtitle && <p className="text-[12px] text-zinc-500 mt-0.5">{subtitle}</p>}
    </div>
  );
}

function NavButtons({ onNext, nextLabel, nextBusy, nextDisabled, nextIcon, testidPrefix }) {
  return (
    <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
      <Button size="sm" className="rounded-sm h-8 bg-black hover:bg-zinc-800 text-white"
        onClick={onNext} disabled={nextBusy || nextDisabled}
        data-testid={`${testidPrefix}-next`}>
        {nextBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : (nextIcon || <ArrowRight className="h-3.5 w-3.5 mr-1" />)}
        {nextLabel}
      </Button>
    </div>
  );
}
